import { useTeamTree } from '@internal/backstage-plugin-common-library-react';
import { withInvalidSupOrgTeam } from '@internal/plugin-watchtower-common';
import { ChevronDown } from 'lucide-react';
import { DateTime } from 'luxon';
import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Content,
  Header,
  LinkButton,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Grid,
  Box,
  Card,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { SCORECARD_HIERARCHY } from '../../components/ScorecardCategories/scorecardHierarchy';
import { TeamScorecardSummary } from '../../components/TeamScorecardSummary';
import { useGetCategories, useGetCategoriesData } from '../../hooks';
import {
  getScorecardItems,
  toStandardISO,
  useScorecardLinkBuilder,
} from '../../utils';
import { getAggregatedScorecardItems } from '../../utils/scorecardHierarchyUtils';
import { ScorecardProgress } from '../../widgets/ScorecardProgress';

const useStyles = makeStyles(theme => ({
  navButton: {
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    borderRadius: '8px',
  },
  menuPaper: {
    minWidth: 250,
    marginTop: theme.spacing(1),
    boxShadow: theme.shadows[4],
    borderRadius: '8px',
  },
  menuHeader: {
    padding: theme.spacing(1, 2),
    fontSize: '0.75rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  cardRoot: {
    borderRadius: '8px',
    backgroundColor: theme.palette.background.paper,
    overflow: 'visible',
  },
  topSection: {
    padding: theme.spacing(3),
    position: 'relative',
  },
  navWrapper: {
    position: 'absolute',
    top: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 10,
  },
  divider: {
    margin: theme.spacing(0, 3),
  },
}));

interface Team {
  id: string;
  name: string;
  title: string;
}

const TeamsMenu = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();
  const buildLink = useScorecardLinkBuilder();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userTeams, setUserTeams] = useState<Team[]>([]);

  const handleTeamClick = (teamId: string) => {
    navigate(buildLink(`/catalog/default/group/${teamId}/scorecards`));
    setAnchorEl(null);
  };

  useEffect(() => {
    const fetchUserTeams = async () => {
      const { ownershipEntityRefs } = await identityApi.getBackstageIdentity();
      const teamRefs = ownershipEntityRefs.filter(ref =>
        ref.startsWith('group:'),
      );
      const teamEntities = await Promise.all(
        teamRefs.map(ref => catalogApi.getEntityByRef(ref)),
      );
      const teams = teamEntities
        .filter(entity => entity !== undefined)
        .map(entity => ({
          id: entity?.metadata.name ?? '',
          name: (entity?.metadata.name ?? '')
            .split('-')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '),
          title: (entity?.metadata.title || entity?.metadata.name) ?? '',
        }));
      setUserTeams(teams);
    };
    fetchUserTeams();
  }, [identityApi, catalogApi]);

  return (
    <>
      <LinkButton
        variant="outlined"
        color="primary"
        className={classes.navButton}
        onClick={e => setAnchorEl(e.currentTarget)}
        startIcon={
          <ChevronDown
            size={16}
            style={{
              transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        }
        to=""
      >
        View My Teams
      </LinkButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{ paper: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Typography className={classes.menuHeader}>My Teams</Typography>
        <Divider />
        {userTeams.length === 0 ? (
          <MenuItem disabled>No teams found</MenuItem>
        ) : (
          userTeams.map(team => (
            <MenuItem key={team.id} onClick={() => handleTeamClick(team.id)}>
              {team.title}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export const OperationalExcellencePage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const rootOrgId = configApi.getString('app.allScorecardsRootTeam');
  const { teamTree } = useTeamTree(rootOrgId);

  // Company-level request: SUP_INVALID collects repositories outside the company
  // hierarchy. Only the Repository Ownership category receives it.
  const companyTeamTree = useMemo(
    () =>
      teamTree?.tree?.team_id
        ? withInvalidSupOrgTeam(teamTree.tree)
        : teamTree?.tree,
    [teamTree],
  );

  const endDate = useMemo(() => toStandardISO(DateTime.now()), []);
  const startDate = useMemo(
    () => toStandardISO(DateTime.now().minus({ months: 5 }).startOf('month')),
    [],
  );

  const { value: categories } = useGetCategories();
  const { value: categoriesData } = useGetCategoriesData(
    companyTeamTree,
    startDate,
    endDate,
    'month',
  );

  const { flatScorecardItems, groupedScorecardItems } = useMemo(() => {
    if (!categoriesData)
      return { flatScorecardItems: null, groupedScorecardItems: null };

    const rawItems = getScorecardItems(categoriesData, 'month');
    const aggregatedItems = getAggregatedScorecardItems(
      rawItems,
      SCORECARD_HIERARCHY,
    );

    return {
      flatScorecardItems: rawItems,
      groupedScorecardItems: aggregatedItems,
    };
  }, [categoriesData]);

  const isLoading = !teamTree || !categoriesData;

  const cardShadow =
    theme.palette.type === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.3)'
      : '0 2px 8px rgba(0,0,0,0.08)';

  return (
    <Page themeId="tool">
      <Header
        title="Scorecards"
        subtitle="Organisation-level scorecard health and compliance tracking across all teams."
      />
      <Content>
        {isLoading ? (
          <Progress />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card
                className={classes.cardRoot}
                style={{ boxShadow: cardShadow }}
              >
                <Box className={classes.topSection}>
                  <Box className={classes.navWrapper}>
                    <TeamsMenu />
                  </Box>

                  <ScorecardProgress
                    scorecardItems={groupedScorecardItems}
                    flatScorecardItems={flatScorecardItems}
                  />
                  <Divider
                    className={classes.divider}
                    style={{ marginTop: theme.spacing(1) }}
                  />
                  <Box style={{ marginTop: theme.spacing(1) }}>
                    <TeamScorecardSummary
                      categories={categories}
                      categoriesData={categoriesData}
                      title="Scorecards List"
                    />
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
